import { generate } from 'shortid';
import { SetLH, SetRH, FlipHorizontal, FlipVertical, GetAbsLH, GetAbsRH, isKnotInRectangle,
  reflectKnot, isDart, isDartApex, createKnot, setHandle, getInverseType, setCorner, clearHandle,
  GetRelLH, GetRelRH } from './KnotHelper';
import SegmentHelper from './SegmentHelper';
import { getTagentTLength, convertPointPairToBezierJS, getLastIntersection, convertSegmentIntoBezierCurves,
  intersectCurveByLine } from './BezierHelper';
import SVGHelper from './SVGHelper';
import MathHelper from './MathHelper';
import DrillHoleHelper from './DrillHoleHelper';
import InternalLineHelper from './InternalLineHelper';
import { DART_LEFT, DART_RIGHT } from '../constants/knotTypes';
export default class PieceHelper {
  static getLastKnot(piece) {
    if (!piece.knots && piece.knots < 1) {
      return null;
    }
    return piece.knots[piece.knots.length - 1];
  }

  static createPieceTitle(pieces, alias = '') {
    const existTitle = (title) => pieces.find(((piece) => title === piece.get('title')));

    const getTitle = (strTitle, n = 1) => {
      const newStrTitle = strTitle || 'Piece';

      if (newStrTitle !== 'Piece' && !existTitle(newStrTitle)) return strTitle;

      const title = `${newStrTitle}${n}`;

      return existTitle(title) ? getTitle(newStrTitle, n + 1) : title;
    };

    return getTitle(alias);
  }

  static createPiece(id = generate(), knots = [], isClosed = false, counter = 0, title = '', rotation = 0) {
    const seam = [];
    const notches = [];
    const labels = [];
    const matches = [];
    const drillHoles = {};
    const internalLines = {};
    const mirror = null;
    const pair = false;
    const arrowHead = false;
    const arrowTail = false;
    const isInside = false;
    const piecesInside = new Set();
    const inlineImages = null;
    const grain = 0;

    return {
      id,
      knots,
      isClosed,
      counter,
      seam,
      notches,
      labels,
      matches,
      drillHoles,
      title,
      rotation,
      mirror,
      internalLines,
      pair,
      arrowHead,
      arrowTail,
      isInside,
      piecesInside,
      inlineImages,
      grain,
    };
  }

  static addInlineImage(piece, img) {
    return { ...piece, inlineImages: img };
  }

  static changePair(piece) {
    // eslint-disable-next-line no-param-reassign
    piece.pair = !piece.pair;
  }

  static changeBothArrow(piece) {
    const { arrowHead, arrowTail } = piece;
    if (arrowHead && arrowTail) {
      return false;
    }
    return true;
  }

  static changeArrowHead(piece) {
    const { arrowHead } = piece;
    return !arrowHead;
  }

  static changeArrowTail(piece) {
    const { arrowTail } = piece;
    return !arrowTail;
  }

  static getKnotIndex(piece, id, isImmutable = false) {
    if (!isImmutable) return piece.knots.findIndex((knot) => knot.id === id);

    return piece.get('knots').findIndex((knot) => knot.get('id') === id);
  }

  static getKnotIds(piece) {
    return piece.knots.filter((knot) => !knot.type || !knot.type.includes('DART')).map((knot) => knot.id);
  }

  static getPrevKnotId(piece, id, times = 1) {
    let prevKnotId = id;

    for (let i = 0; i < times; i += 1) {
      const prevKnot = this.getPrevKnot(piece, { id: prevKnotId });

      if (!prevKnot) {
        return null;
      }

      prevKnotId = prevKnot.id;
    }

    return prevKnotId;
  }

  static getPrevKnot(piece, knot) {
    const { knots } = piece;

    const knotIndex = this.getKnotIndex(piece, knot.id);

    if (knotIndex === -1) return null;

    if (knotIndex === 0) {
      return piece.isClosed ? knots[knots.length - 1] : null;
    }

    return knots[knotIndex - 1];
  }

  static getNextKnotId(piece, id, times = 1) {
    let nextKnotId = id;

    for (let i = 0; i < times; i += 1) {
      const nextKnot = this.getNextKnot(piece, { id: nextKnotId });

      if (!nextKnot) {
        return null;
      }

      nextKnotId = nextKnot.id;
    }

    return nextKnotId;
  }

  static getNextKnot(piece, knot) {
    const { knots } = piece;

    const knotIndex = this.getKnotIndex(piece, knot.id);

    if (knotIndex === -1) return null;

    if (knotIndex === knots.length - 1) {
      return piece.isClosed ? knots[0] : null;
    }

    return knots[knotIndex + 1];
  }

  static getPrevCorner(piece, knot) {
    const corners = this.getCorners(piece);
    const cornerIndex = corners.findIndex((corner) => corner.id === knot.id);

    if (cornerIndex === -1) {
      let prevKnot = this.getPrevKnot(piece, knot);

      while (prevKnot && prevKnot.id !== knot.id) {
        if (prevKnot.corner) {
          return prevKnot;
        }

        prevKnot = this.getPrevKnot(piece, prevKnot);
      }

      return null;
    }

    if (cornerIndex === 0) {
      return piece.isClosed ? corners[corners.length - 1] : null;
    }

    return corners[cornerIndex - 1];
  }

  static getPrevCornerId(piece, id) {
    const prevCorner = this.getPrevCorner(piece, { id });
    return prevCorner ? prevCorner.id : null;
  }

  static getNextCornerId(piece, id) {
    const nextCorner = this.getNextCorner(piece, { id });
    return nextCorner ? nextCorner.id : null;
  }

  static getNextCorner(piece, knot) {
    const corners = this.getCorners(piece);
    const cornerIndex = corners.findIndex((corner) => corner.id === knot.id);

    if (cornerIndex === -1) {
      let nextKnot = this.getNextKnot(piece, knot);

      while (nextKnot && nextKnot.id !== knot.id) {
        if (nextKnot.corner) {
          return nextKnot;
        }

        nextKnot = this.getNextKnot(piece, nextKnot);
      }

      return null;
    }

    if (cornerIndex === corners.length - 1) {
      return piece.isClosed ? corners[0] : null;
    }

    return corners[cornerIndex + 1];
  }

  static getCorners(piece) {
    const corners = piece.knots.filter((knot) => knot.corner && !isDart(knot));
    return corners;
  }

  static getCornersWithDarts(piece) {
    const corners = piece.knots.filter((knot) => knot.corner && !isDartApex(knot));
    return corners;
  }

  static getSegment(piece, id) {
    let segment = [];
    const { knots } = piece;
    const knot = knots.find((k) => k.id === id);
    const startCornerIndex = knot && knot.corner ? this.getKnotIndex(piece, id) : this.getKnotIndex(piece, this.getPrevCornerId(piece, id));
    const endCornerIndex = this.getKnotIndex(piece, this.getNextCornerId(piece, id));

    if (endCornerIndex > startCornerIndex) {
      segment = piece.knots.slice(startCornerIndex, endCornerIndex + 1);
    } else {
      segment = knots.slice(startCornerIndex).concat(knots.slice(0, endCornerIndex + 1));
    }
    return segment;
  }

  static getNextNormalCornerIndex(corners, position) {
    for (let i = position; i < corners.length; i += 1) {
      if (!corners[i].type || !corners[i].type.includes('DART')) {
        return i;
      }
    }
    return 0;
  }

  static getSegments(piece, withDart = false) {
    const { isClosed } = piece;
    const knots = piece.knots.slice();
    let corners = this.getCorners(piece);
    if (withDart) {
      corners = this.getCornersWithDarts(piece);
    }
    const segmentsData = [];

    if (knots.length === 1) return segmentsData;

    if (corners.length === 0) {
      if (isClosed) {
        segmentsData.push(knots.concat(knots.slice(0, 1)));
      } else {
        segmentsData.push(knots);
      }
    } else if (corners.length === 1) {
      const cornerIndex = this.getKnotIndex(piece, corners[0].id);
      if (isClosed) {
        segmentsData.push(knots.slice(cornerIndex).concat(knots.slice(0, cornerIndex + 1)));
      } else {
        segmentsData.push(knots);
      }
    } else {
      for (let i = 0; i < corners.length - 1; i += 1) {
        if (withDart) {
          if (corners[i + 1].type === DART_LEFT && corners[i].type !== DART_RIGHT) {
            let position = this.getNextNormalCornerIndex(corners, i + 1);
            if (position !== 0) {
              const startCornerIndex = this.getKnotIndex(piece, corners[i].id);
              const endCornerIndex = this.getKnotIndex(piece, corners[position].id);
              segmentsData.push(knots.slice(startCornerIndex, endCornerIndex + 1));
            } else {
              position = this.getNextNormalCornerIndex(corners, 0);
              const startCornerIndex = this.getKnotIndex(piece, corners[i].id);
              const endCornerIndex = this.getKnotIndex(piece, corners[position].id);
              segmentsData.push(knots.slice(startCornerIndex, knots.length).concat(knots.slice(0, endCornerIndex + 1)));
            }
          } else if (corners[i].type !== DART_RIGHT) {
            const startCornerIndex = this.getKnotIndex(piece, corners[i].id);
            const endCornerIndex = this.getKnotIndex(piece, corners[i + 1].id);
            segmentsData.push(knots.slice(startCornerIndex, endCornerIndex + 1));
          }
        } else {
          const startCornerIndex = this.getKnotIndex(piece, corners[i].id);
          const endCornerIndex = this.getKnotIndex(piece, corners[i + 1].id);
          segmentsData.push(knots.slice(startCornerIndex, endCornerIndex + 1));
        }
      }

      const index = this.getNextNormalCornerIndex(corners, 0);
      const lastCornerIndex = this.getKnotIndex(piece, corners[corners.length - 1].id);
      const firstCornerIndex = this.getKnotIndex(piece, corners[index].id);
      if (knots.length > 2 && isClosed && knots[lastCornerIndex].type !== DART_RIGHT) {
        segmentsData.push(knots.slice(lastCornerIndex).concat(knots.slice(0, firstCornerIndex + 1)));
      } else {
        if (firstCornerIndex !== 0) {
          segmentsData.unshift(knots.slice(0, firstCornerIndex + 1));
        }
        if (lastCornerIndex !== knots.length - 1) {
          segmentsData.push(knots.slice(lastCornerIndex, knots.length + 1));
        }
      }
    }
    return segmentsData;
  }

  static getNotches(piece, scale = 1) {
    const notchesList = [];
    const { notches, seam } = piece;
    const corners = this.getCorners(piece);

    corners.forEach((corner) => {
      const segment = this.getSegment(piece, corner.id);
      const cornerNotches = notches.filter((notch) => notch.knotId === corner.id);
      let notchSeam = seam.find((s) => s.knotId === corner.id);
      notchSeam = notchSeam && notchSeam.allowance !== 0 ? notchSeam.allowance : null;

      cornerNotches.forEach((notch) => {
        const pointPair = SegmentHelper.getNotchPointPair(segment, notch.distance * scale, notchSeam);

        if (pointPair) {
          notchesList.push(pointPair);
        }
      });
    });

    return notchesList;
  }

  static getSeam(piece) {
    const { seam } = piece;
    let paths = [];

    seam.forEach((element) => {
      const { knotId, allowance } = element;

      if (allowance === 0) return;

      const segment = this.getSegment(piece, knotId);
      let offset = SegmentHelper.getSegmentOffset(segment, allowance);
      const prevCornerId = this.getPrevCornerId(piece, knotId);
      const nextCornerId = this.getNextCornerId(piece, knotId);
      const prevSeam = prevCornerId ? seam.find((s) => s.knotId === prevCornerId && s.allowance !== 0) : null;
      const nextSeam = nextCornerId ? seam.find((s) => s.knotId === nextCornerId && s.allowance !== 0) : null;

      if (prevSeam) {
        const offsetFirst = offset[0];
        const prevSegment = this.getSegment(piece, prevCornerId);
        const prevOffset = SegmentHelper.getSegmentOffset(prevSegment, prevSeam.allowance);
        const prevOffsetLast = prevOffset[prevOffset.length - 1];
        let angle = MathHelper.getAngleLines({ p1: segment[0], p2: GetAbsRH(segment[0]) }, { p1: segment[0], p2: GetAbsLH(segment[0]) });
        angle = MathHelper.convertRadiansToDegrees(angle);

        if (angle <= 25) {
          // Add line between previous and current offset.
          const line = convertPointPairToBezierJS(prevOffsetLast.get(1), offsetFirst.get(0));

          paths = paths.concat([line]);
        } else if (angle < 180) {
          /* Extend previous and current offset, find intersection between them and cut
            leftovers to add the corner. If there's no intersection, add a line between offsets. */
          const extPrevOffset = getTagentTLength(prevOffsetLast, 1, 500);
          const extOffset = getTagentTLength(offsetFirst, 0, -500);
          const extPrevLine = convertPointPairToBezierJS(extPrevOffset, prevOffsetLast.get(1));
          const extLine = convertPointPairToBezierJS(extOffset, offsetFirst.get(0));
          const intersectionEPL = extPrevLine.intersects({ p1: extOffset, p2: offsetFirst.get(0) }); // TODO: Add this library https://github.com/thelonious/kld-intersections
          const intersectionEL = extLine.intersects({ p1: extPrevOffset, p2: prevOffsetLast.get(1) });

          if (intersectionEPL && intersectionEL && intersectionEPL.length && intersectionEL.length) {
            const prevCut = extPrevLine.split(intersectionEPL[0]).right;
            const cut = extLine.split(intersectionEL[0]).right;

            paths = paths.concat([prevCut, cut]);
          } else {
            const line = convertPointPairToBezierJS(prevOffsetLast.get(1), offsetFirst.get(0));

            paths = paths.concat([line]);
          }
        } else {
          /* Find intersection between previous and current offset and cut both leftovers.
            If there's no intersection, draw a line from the higher offset end to the first knot,
            intersect it with the lower offset, cut that line and add it. If the higher offset is
            the previous, cut the current offset at the beggining */
          let intersection = null;
          for (let i = 0; i < prevOffset.length; i += 1) {
            const bezier = prevOffset[i];
            intersection = getLastIntersection(bezier, offset);

            if (intersection) {
              offset = offset.slice(intersection.bezierIdx);
              offset[0] = offset[0].split(intersection.t[1]).right;
              break;
            }
          }

          if (!intersection) {
            const higherSeam = prevSeam.allowance > element.allowance ? 'prev' : 'current';
            const lowerOffset = higherSeam === 'prev' ? offset : prevOffset;
            const higherCornerPoint = higherSeam === 'prev' ? prevOffsetLast.get(1) : offsetFirst.get(0);
            const higherCornerToKnotLine = convertPointPairToBezierJS(higherCornerPoint, segment[0]);
            intersection = getLastIntersection(higherCornerToKnotLine, lowerOffset);

            if (intersection) {
              const line = higherCornerToKnotLine.split(intersection.t[0]).left;

              paths = paths.concat([line]);

              if (higherSeam === 'prev') {
                offset = offset.slice(intersection.bezierIdx);
                offset[0] = offset[0].split(intersection.t[1]).right;
              }
            }
          }
        }
      }

      if (nextSeam) {
        const nextSegment = this.getSegment(piece, nextCornerId);
        const nextOffset = SegmentHelper.getSegmentOffset(nextSegment, nextSeam.allowance);
        let angle = MathHelper.getAngleLines({ p1: nextSegment[0], p2: GetAbsRH(nextSegment[0]) }, { p1: nextSegment[0], p2: GetAbsLH(nextSegment[0]) });
        angle = MathHelper.convertRadiansToDegrees(angle);

        if (angle > 180) {
          /* Find intersection between current and next offset and cut current offset leftovers.
            If there's no intersection, draw a line from the higher offset end to the last knot,
            intersect it with the lower offset, cut that line and add it. If the higher offset is
            the next, cut the current offset at the end */
          let intersection = null;
          for (let i = 0; i < offset.length; i += 1) {
            const bezier = offset[i];
            intersection = getLastIntersection(bezier, nextOffset);

            if (intersection) {
              offset = offset.slice(0, i + 1);
              offset[offset.length - 1] = offset[offset.length - 1].split(intersection.t[0]).left;
              break;
            }
          }

          if (!intersection) {
            const higherSeam = nextSeam.allowance > element.allowance ? 'next' : 'current';
            const lowerOffset = higherSeam === 'next' ? offset : nextOffset;
            const higherCornerPoint = higherSeam === 'next' ? nextOffset[0].get(0) : offset[offset.length - 1].get(1);
            const higherCornerToKnotLine = convertPointPairToBezierJS(nextSegment[0], higherCornerPoint);
            intersection = getLastIntersection(higherCornerToKnotLine, lowerOffset);

            if (intersection) {
              const line = higherCornerToKnotLine.split(intersection.t[0]).right;

              paths = paths.concat([line]);

              if (higherSeam === 'next') {
                offset = offset.slice(0, intersection.bezierIdx + 1);
                offset[offset.length - 1] = offset[offset.length - 1].split(intersection.t[1]).left;
              }
            }
          }
        }
      }

      paths = paths.concat(offset);
    });

    return paths;
  }

  static getOffset(data, d) {
    if (!data) return null;

    const { knots, isClosed } = data.piece;

    if (isClosed) {
      knots.push(knots[0]);
    }

    return SegmentHelper.getSegmentOffset(knots, d);
  }

  static getOffsetPath(piece, d) {
    const offsetData = this.getOffset(piece, d);
    return SVGHelper.createSVGPathFromBezierJS(offsetData);
  }

  static interpolateKnots(piece) {
    return {
      ...piece,
      knots: SegmentHelper.interpolateKnots(piece.knots, piece.isClosed),
    };
  }

  static immutableInterpolateKnots(piece) {
    return piece.set('knots', SegmentHelper.immutableInterpolateKnots(piece.get('knots'), piece.get('isClosed')));
  }

  static scaleAxis(piece, scale, axis) {
    const { knots, seam, drillHoles } = piece;
    const a = axis || this.getPieceCenter(piece);

    return {
      ...piece,
      knots: knots.map((knot) => {
        const { lH, rH, lHI, rHI } = knot;
        const position = MathHelper.interpolatePoints(knot, a, scale);

        return {
          ...knot,
          x: a.x - position.x,
          y: a.y - position.y,
          lH: lHI ? MathHelper.interpolatePoints(lH, a, scale) : MathHelper.scalarMultiplicationPoint(lH, scale),
          rH: rHI ? MathHelper.interpolatePoints(rH, a, scale) : MathHelper.scalarMultiplicationPoint(rH, scale),
        };
      }),
      seam: seam.map((s) => ({ ...s, allowance: s.allowance * scale })),
      drillHoles: drillHoles && Object.keys(drillHoles).length
        ? Object.assign({}, ...Object.entries(drillHoles).map(([key, drillHole]) => {
          const position = MathHelper.interpolatePoints(drillHole, a, scale);
          return { [key]: { ...drillHole, x: a.x - position.x, y: a.y - position.y } };
        })) : null,
    };
  }

  static scaleDimensionAxis(piece, scale, dimension = 'WIDTH', axis) {
    const { knots, drillHoles } = piece;
    const a = axis || this.getPieceCenter(piece);
    const coord = dimension === 'WIDTH' ? 'x' : 'y';

    return {
      ...piece,
      knots: knots.map((knot) => {
        const { x, y, lH, rH, lHI, rHI } = knot;
        const dimensionAxis = dimension === 'WIDTH' ? { x: a.x, y } : { x, y: a.y };

        const position = MathHelper.interpolatePoints(knot, dimensionAxis, scale);

        return {
          ...knot,
          [coord]: a[coord] - position[coord],
          lH: lHI ? MathHelper.interpolatePoints(lH, dimensionAxis, scale) : { ...lH, [coord]: lH[coord] * scale },
          rH: rHI ? MathHelper.interpolatePoints(rH, dimensionAxis, scale) : { ...rH, [coord]: rH[coord] * scale },
        };
      }),
      drillHoles: drillHoles && Object.keys(drillHoles).length
        ? Object.assign({}, ...Object.entries(drillHoles).map(([key, drillHole]) => {
          const dimensionAxis = dimension === 'WIDTH' ? { x: a.x, y: drillHole.y } : { x: a.x, y: drillHole.y };
          const position = MathHelper.interpolatePoints(drillHole, dimensionAxis, scale);
          return { [key]: { ...drillHole, [coord]: dimensionAxis[coord] - position[coord] } };
        })) : null,
    };
  }

  /**
   * The previous getBounds function converts everything to beizers, and then looks at every point. 
   * We were getting exceptions, and I think its too slow. Here I switch to just getting the bounds of the knots
   * which is less precies, but its a comprimise we can make as bounds never need to be pixel perfect
   * https://codeburst.io/javascript-finding-minimum-and-maximum-values-in-an-array-of-objects-329c5c7e22a2
   */
  static getBoundsFast(piece) {
    const { knots } = piece;
    let x = knots[0].x;
    let y = knots[0].y;
    let width = 10;
    let height = 10;
    let len = knots.length;

    for (let i = 1; i < len; i++) {
      x = Math.min(knots[i].x, x);
      y = Math.min(knots[i].y, y);
      width = Math.max(knots[i].x - x, width);
      height = Math.max(knots[i].y - y, height);
    }

    return { x: x, y: y, w: width, h: height};
  }

  static getBounds(piece) {
    const segments = this.getSegments(this.interpolateKnots(piece));
    const pieceBounds = segments.reduce((bounds, segment) => {
      const seam = piece.seam.find((s) => s.knotId === segment[0].id);
      const segmentBounds = SegmentHelper.getBounds(segment, seam);
      const { x, y, w, h } = segmentBounds;

      const xMin = x < bounds.xMin ? x : bounds.xMin;
      const xMax = x + w > bounds.xMax ? x + w : bounds.xMax;
      const yMin = y < bounds.yMin ? y : bounds.yMin;
      const yMax = y + h > bounds.yMax ? y + h : bounds.yMax;

      return { xMin, xMax, yMin, yMax };
    }, { xMin: Number.MAX_VALUE, xMax: 0, yMin: Number.MAX_VALUE, yMax: 0 });

    const { xMin, xMax, yMin, yMax } = pieceBounds;

    if (xMax === 0 || yMax === 0) return { x: xMin, y: yMin, w: 0, h: 0 };

    return { x: xMin, y: yMin, w: (xMax - xMin), h: (yMax - yMin) };
  }

  static getBoundsKnots(knots) {
    const pieceBounds = knots.reduce((bounds, knot) => {
      const { x, y } = knot;

      const xMin = x < bounds.xMin ? x : bounds.xMin;
      const xMax = x > bounds.xMax ? x : bounds.xMax;
      const yMin = y < bounds.yMin ? y : bounds.yMin;
      const yMax = y > bounds.yMax ? y : bounds.yMax;

      return { xMin, xMax, yMin, yMax };
    }, { xMin: Number.MAX_VALUE, xMax: 0, yMin: Number.MAX_VALUE, yMax: 0 });

    const { xMin, xMax, yMin, yMax } = pieceBounds;

    return { x: xMin, y: yMin, w: (xMax - xMin), h: (yMax - yMin) };
  }

  static isClockwise(piece) {
    return this.getApproximateArea(piece) > 0;
  }

  static getApproximateArea(piece) {
    let area = 0;
    const { knots } = piece;
    knots.forEach((k, idx) => {
      const j = (idx + 1) % knots.length;
      area += k.x * knots[j].y;
      area -= knots[j].x * k.y;
    });
    return area / 2;
  }

  static getPieceCenter(piece) {
    const b = this.getBounds(this.interpolateKnots(piece));

    return { x: b.x + (b.w / 2), y: b.y + (b.h / 2) };
  }

  /**
   * Gets the center of the piece based on the positions of the knots
   * This doesnt change based on rotation like the current getCenter method does
   */
  // This function does not return the real centroid of the polygon but the average of the knots.
  // static getCentroid(piece) {
  //   return piece.knots.reduce((center, k) => ({ x: center.x + (k.x / piece.knots.length), y: center.y + (k.y / piece.knots.length) }),
  //     { x: 0, y: 0 });
  // }

  static getCentroid(piece) {
    const { knots } = piece;
    const centroid = { x: 0, y: 0 };
    let signedArea = 0.0;
    let x0 = 0.0; // Current vertex X
    let y0 = 0.0; // Current vertex Y
    let x1 = 0.0; // Next vertex X
    let y1 = 0.0; // Next vertex Y
    let a = 0.0; // Partial signed area


    const { mirror } = piece;

    // For all vertices except last
    let i = 0;
    for (i = 0; i < knots.length - 1; i += 1) {
      x0 = knots[i].x;
      y0 = knots[i].y;
      x1 = knots[i + 1].x;
      y1 = knots[i + 1].y;
      a = x0 * y1 - x1 * y0;
      signedArea += a;
      centroid.x += (x0 + x1) * a;
      centroid.y += (y0 + y1) * a;
    }

    // Do last vertex separately to avoid performing an expensive
    // modulus operation in each iteration.
    x0 = knots[i].x;
    y0 = knots[i].y;
    x1 = knots[0].x;
    y1 = knots[0].y;
    a = x0 * y1 - x1 * y0;
    signedArea += a;
    centroid.x += (x0 + x1) * a;
    centroid.y += (y0 + y1) * a;

    signedArea *= 0.5;
    centroid.x /= (6.0 * signedArea);
    centroid.y /= (6.0 * signedArea);

    return centroid;
  }

  static flipHorizontal(piece, xAxis) {
    let { knots, drillHoles, internalLines } = piece;

    knots = knots.map((knot) => FlipHorizontal(knot, xAxis));

    if (drillHoles && Object.keys(drillHoles).length) {
      drillHoles = Object.assign({}, ...Object.entries(drillHoles)
        .map(([k, v]) => ({ [k]: DrillHoleHelper.flipHorizontal(v, xAxis) })));
    }

    if (internalLines && Object.keys(internalLines).length) {
      internalLines = Object.assign({}, ...Object.entries(internalLines)
        .map(([k, v]) => ({ [k]: InternalLineHelper.flipHorizontal(v, xAxis) })));
    }

    const flippedPiece = {
      ...piece,
      knots,
      drillHoles,
      internalLines,
    };

    return this.reverse(flippedPiece);
  }

  static flipVertical(piece, yAxis) {
    let { knots, drillHoles, internalLines } = piece;

    knots = knots.map((knot) => FlipVertical(knot, yAxis));

    if (drillHoles && Object.keys(drillHoles).length) {
      drillHoles = Object.assign({}, ...Object.entries(drillHoles)
        .map(([k, v]) => ({ [k]: DrillHoleHelper.flipVertical(v, yAxis) })));
    }

    if (internalLines && Object.keys(internalLines).length) {
      internalLines = Object.assign({}, ...Object.entries(internalLines)
        .map(([k, v]) => ({ [k]: InternalLineHelper.flipVertical(v, yAxis) })));
    }

    const flippedPiece = {
      ...piece,
      knots,
      drillHoles,
      internalLines,
    };

    return this.reverse(flippedPiece);
  }

  static reverse(piece, reverseKnots = true) {
    let { knots } = piece;

    if (reverseKnots) {
      knots = SegmentHelper.reverse(knots)
        .map((knot) => ({ ...knot, type: getInverseType(knot) }));
    }

    let reversedPiece = this.interpolateKnots({ ...piece, knots });

    reversedPiece = this.reverseSeam(reversedPiece);
    reversedPiece = this.reverseLabels(reversedPiece);
    reversedPiece = this.reverseMatches(reversedPiece);
    reversedPiece = this.reverseNotches(reversedPiece);
    reversedPiece = this.reverseMirror(reversedPiece);

    return reversedPiece;
  }

  static reverseMirror(piece) {
    const { mirror } = piece;

    if (!mirror) return piece;

    return { ...piece, mirror: this.getPrevCornerId(piece, mirror) };
  }

  static reverseSeam(piece) {
    return {
      ...piece,
      seam: piece.seam.map((seam) => ({ ...seam, knotId: this.getPrevCornerId(piece, seam.knotId) })), // Set the seam to the previous corner.
    };
  }

  static reverseLabels(piece) {
    return {
      ...piece,
      labels: piece.labels.map((label) => ({
        ...label,
        knotId: this.getPrevCornerId(piece, label.knotId),
        inverse: label.label !== '' ? !label.inverse : false, // Toggle label inverse to keep consistency
      })),
    };
  }

  static reverseMatches(piece) {
    return {
      ...piece,
      matches: piece.matches.map((match) => ({ ...match, knotId: this.getPrevCornerId(piece, match.knotId) })), // Set the match to the previous corner.
    };
  }

  static reverseNotches(piece) {
    return {
      ...piece,
      notches: piece.notches.map((notch) => {
        const { knotId, distance } = notch;
        const prevCornerId = this.getPrevCornerId(piece, knotId);
        const segment = this.getSegment(piece, prevCornerId);

        return {
          ...notch,
          knotId: prevCornerId,
          distance: SegmentHelper.getLength(segment) - distance,
        };
      }),
    };
  }

  static deltaPiece(piece, delta) {
    const knots = piece.knots.map((knot) => ({
      ...knot,
      x: knot.x + delta.x,
      y: knot.y + delta.y,
    }));

    let { drillHoles, internalLines } = piece;

    if (drillHoles && Object.keys(drillHoles).length) {
      drillHoles = Object.assign({}, ...Object.entries(drillHoles)
        .map(([k, drillHole]) => ({ [k]: { ...drillHole, x: drillHole.x + delta.x, y: drillHole.y + delta.y } })));
    }

    if (internalLines && Object.keys(internalLines).length) {
      internalLines = Object.assign({}, ...Object.entries(internalLines)
        .map(([k, internalLine]) => ({ [k]:
        {
          ...internalLine,
          knots: internalLine.knots.map((knot) => ({ ...knot, x: knot.x + delta.x, y: knot.y + delta.y })),
        },
        })));
    }

    return {
      ...piece,
      knots,
      drillHoles,
      internalLines,
    };
  }

  static deltaKnots(piece, knots, delta) {
    const resultPiece = {
      ...piece,
      knots: piece.knots.map((knot) => {
        if (!knots.find((k) => k === knot.id)) return knot;

        return {
          ...knot,
          x: knot.x + delta.x,
          y: knot.y + delta.y,
        };
      }),
    };

    return this.interpolateKnots(resultPiece);
  }

  static changeKnotCorner(piece, knotId) {
    const { knots } = piece;
    const knotIndex = knots.findIndex(({ id }) => id === knotId);

    if (knotIndex < 0) return piece;

    const knotById = knots[knotIndex];
    knots[knotIndex] = setCorner(knotById, !knotById.corner);

    if (!knotById.corner) {
      ['seam', 'labels', 'matches', 'notches'].forEach((pieceArrProperty) => {
        piece[pieceArrProperty] = piece[pieceArrProperty].filter((element) => element.knotId !== knotId); // eslint-disable-line no-param-reassign
      });

      if (piece.mirror === knotId) piece.mirror = null; // eslint-disable-line no-param-reassign
    }

    return piece;
  }

  static deleteKnot(piece, knotId) {
    let { knots, seam, labels, matches, notches, internalLines, mirror } = piece;
    knots = knots.slice();
    const idx = this.getKnotIndex(piece, knotId);
    knots.splice(idx, 1);

    seam = seam.filter((s) => s.knotId !== knotId);
    labels = labels.filter((label) => label.knotId !== knotId);
    matches = matches.filter((match) => match.knotId !== knotId);
    notches = notches.filter((notch) => notch.knotId !== knotId);

    if (mirror && mirror === knotId) mirror = null;

    internalLines = internalLines && Object.keys(internalLines).length
      ? Object.assign({}, ...Object.values(internalLines).filter((internalLine) => internalLine.startWeld !== knotId && internalLine.endWeld !== knotId).map((internalLine) => ({ [internalLine.id]: internalLine }))) : {};

    return {
      ...piece,
      knots,
      seam,
      labels,
      matches,
      notches,
      mirror,
      internalLines,
    };
  }

  static getMajorSeamAllowance(piece) {
    let allowance = 0;
    piece.seam.forEach((seam) => {
      if (seam.allowance > allowance) {
        ({ allowance } = seam);
      }
    });
    return allowance;
  }

  static getLabelSegment(piece, label) {
    const pieceLabel = piece.labels.find((l) => l.label === label);
    return pieceLabel ? this.getSegment(piece, pieceLabel.knotId) : null;
  }

  static getLabelStartKnot(piece, label) {
    const pieceLabel = piece.labels.find((l) => l.label === label);

    if (pieceLabel) {
      const segment = this.getSegment(piece, pieceLabel.knotId);
      return pieceLabel.inverse ? segment[segment.length - 1] : segment[0];
    }

    return null;
  }

  static getLabel(piece, label) {
    return piece.labels.find((l) => l.label === label);
  }

  static getLabels(piece, excludeLabelsWithExtensions = false) {
    return piece.labels.reduce((labels, { label, extensions }) => label !== '' && !(extensions.length && excludeLabelsWithExtensions) ? [...labels, label] : labels, []);
  }

  static getExtensions(piece) {
    return piece.labels.reduce((pieceExtensions, { extensions }) => [...pieceExtensions, ...extensions], []);
  }

  static getLastExtension(piece, knotId) {
    const label = piece.labels.find((l) => l.knotId === knotId);

    if (label && label.extensions && label.extensions.length) {
      return label.extensions[label.extensions.length - 1];
    }

    return null;
  }

  static getMatches(piece) {
    if (!piece.matches) return [];

    return piece.matches.reduce((matchLabels, match) => (match.label !== '') ? [...matchLabels, match.label] : matchLabels, []);
  }

  static isPieceInRectangle(piece, rectangle) {
    const { knots } = piece;
    for (let i = 0; i < knots.length; i += 1) {
      const knot = knots[i];
      if (isKnotInRectangle(knot, rectangle)) return true;
    }
    return false;
  }

  static changeIds(piece) {
    let { knots, drillHoles, internalLines, mirror } = piece;
    const { seam, labels, matches, notches } = piece;
    const ids = Object.assign({}, ...knots.map((knot) => ({ [knot.id]: generate() })));

    knots = knots.map((knot) => ({ ...knot, id: ids[knot.id] }));
    drillHoles = drillHoles && Object.keys(drillHoles).length ? Object.assign({}, ...Object.values(drillHoles)
      .map((drillHole) => {
        const id = generate();
        return ({ [id]: { ...drillHole, id } });
      })) : {};
    internalLines = internalLines && Object.keys(internalLines).length ? Object.assign({}, ...Object.values(internalLines)
      .map((internalLine) => {
        const { startWeld, endWeld } = internalLine;
        const id = generate();

        return ({ [id]: {
          ...internalLine,
          id,
          startWeld: ids[startWeld],
          endWeld: ids[endWeld],
          knots: internalLine.knots.map((knot) => ({ ...knot, id: generate() })),
        } });
      })) : {};

    mirror = mirror ? ids[mirror] : null;

    const updateKnotIds = (array) => array.map((element) => ({ ...element, knotId: ids[element.knotId] }));

    return {
      ...piece,
      id: generate(),
      knots,
      seam: updateKnotIds(seam),
      labels: updateKnotIds(labels),
      matches: updateKnotIds(matches),
      notches: updateKnotIds(notches),
      drillHoles,
      mirror,
      internalLines,
    };
  }

  static getReflectedPiece(piece) {
    const { knots, mirror, drillHoles, internalLines } = piece;

    const getKnotById = (id) => knots.find((knot) => knot.id === id);

    if (!mirror) return piece;

    const axisStart = getKnotById(mirror);

    if (!axisStart) return piece;

    const axisEnd = PieceHelper.getNextCorner(piece, axisStart);

    let reflectedPiece = { ...piece, knots: knots.map((knot) => reflectKnot(knot, axisStart, axisEnd)).reverse() };
    reflectedPiece = this.interpolateKnots(reflectedPiece);

    if (drillHoles && Object.keys(drillHoles).length) {
      reflectedPiece = {
        ...reflectedPiece,
        drillHoles: Object.assign({}, ...Object.entries(drillHoles)
          .map(([k, drillHole]) => ({ [k]: DrillHoleHelper.reflect(drillHole, axisStart, axisEnd) }))),
      };
    }

    if (internalLines && Object.keys(internalLines).length) {
      reflectedPiece = {
        ...reflectedPiece,
        internalLines: Object.assign({}, ...Object.entries(internalLines)
          .map(([k, internalLine]) => {
            const startWeldKnot = getKnotById(internalLine.startWeld);
            const endtWeldKnot = getKnotById(internalLine.endWeld);

            return { [k]: InternalLineHelper.reflect(internalLine, startWeldKnot, endtWeldKnot, axisStart, axisEnd) };
          })),
      };
    }

    reflectedPiece = this.reverse(reflectedPiece, false);

    return this.changeIds(reflectedPiece);
  }

  static getReflectedPieceInside(pieceInside, axisStart, axisEnd) {
    let reflectedPieceIn = { ...pieceInside, id: generate(), knots: pieceInside.knots.map((knot) => reflectKnot(knot, axisStart, axisEnd)).reverse() };
    reflectedPieceIn = this.interpolateKnots(reflectedPieceIn);
    if (reflectedPieceIn.drillHoles && Object.keys(reflectedPieceIn.drillHoles).length) {
      reflectedPieceIn = {
        ...reflectedPieceIn,
        drillHoles: Object.assign({}, ...Object.entries(reflectedPieceIn.drillHoles)
          .map(([k, drillHole]) => ({ [k]: DrillHoleHelper.reflect(drillHole, axisStart, axisEnd) }))),
      };
    }
    if (reflectedPieceIn.internalLines && Object.keys(reflectedPieceIn.internalLines).length) {
      const getKnotByIdIn = (id) => reflectedPieceIn.knots.find((knot) => knot.id === id);
      reflectedPieceIn = {
        ...reflectedPieceIn,
        internalLines: Object.assign({}, ...Object.entries(reflectedPieceIn.internalLines)
          .map(([k, internalLine]) => {
            const startWeldKnot = getKnotByIdIn(internalLine.startWeld);
            const endtWeldKnot = getKnotByIdIn(internalLine.endWeld);
            return { [k]: InternalLineHelper.reflect(internalLine, startWeldKnot, endtWeldKnot, axisStart, axisEnd) };
          })),
      };
    }
    reflectedPieceIn = this.reverse(reflectedPieceIn, false);
    return this.changeIds(reflectedPieceIn);
  }

  static getIdsBetweenKnots(piece, startId, endId) {
    const ids = [];
    let nextKnotId = this.getNextKnotId(piece, startId);

    while (nextKnotId && nextKnotId !== endId) {
      ids.push(nextKnotId);
      nextKnotId = this.getNextKnotId(piece, nextKnotId);
    }

    return ids;
  }

  static createSquarePiece(id, dimension, position) {
    const { width, height } = dimension;
    const k1 = createKnot(generate(), position.x, position.y, true);
    const k2 = createKnot(generate(), position.x + width, position.y, true);
    const k3 = createKnot(generate(), position.x + width, position.y + height, true);
    const k4 = createKnot(generate(), position.x, position.y + height, true);

    return this.createPiece(id, [k1, k2, k3, k4], true);
  }

  static addKnot(piece, knot, position = 0, prevRH, nextLH) {
    let newPiece = piece;

    if (prevRH) {
      newPiece = this.moveHandle(newPiece, prevRH.id, 'RIGHT', prevRH);
    }

    if (nextLH) {
      newPiece = this.moveHandle(newPiece, nextLH.id, 'LEFT', nextLH);
    }

    const knots = newPiece.knots.slice();
    // If position is 0 or null, add knot at the end.
    if (!position || position >= knots.length) {
      knots.push(knot);
    } else {
      knots.splice(position, 0, knot);
    }

    return {
      ...newPiece,
      knots,
    };
  }

  static moveHandle(piece, knotId, direction, position) {
    const interpolatedPiece = this.interpolateKnots(piece);
    const knots = interpolatedPiece.knots.slice();
    const knotIndex = knots.findIndex((knot) => knot.id === knotId);

    if (knotIndex !== -1) {
      knots[knotIndex] = setHandle(knots[knotIndex], direction, position);
    }

    return {
      ...interpolatedPiece,
      knots,
    };
  }

  static clearKnotHandle(piece, knotId, direction) {
    // TODO: Make array item change method to return new objects.
    const knotIndex = this.getKnotIndex(piece, knotId);
    const knot = clearHandle(piece.knots[knotIndex], direction);
    const knots = piece.knots.slice();

    knots[knotIndex] = knot;

    return {
      ...piece,
      knots,
    };
  }

  static consolidateMirror(piece) {
    const { knots, mirror } = piece;

    if (!mirror) {
      return piece;
    }

    let p = { ...piece, knots: knots.slice() };

    const axisStartIndex = PieceHelper.getKnotIndex(piece, mirror);
    const axisEndId = PieceHelper.getNextCornerId(piece, mirror);
    const axisEndIndex = PieceHelper.getKnotIndex(piece, axisEndId);

    const reflectedPiece = PieceHelper.getReflectedPiece(piece);

    const reflectedAxisEnd = reflectedPiece.knots.find((knot) => knot.id === reflectedPiece.mirror);
    const reflectedAxisEndIndex = PieceHelper.getKnotIndex(reflectedPiece, reflectedAxisEnd.id);
    const reflectedAxisStart = PieceHelper.getNextKnot(reflectedPiece, reflectedAxisEnd);
    const reflectedAxisStartIndex = PieceHelper.getKnotIndex(reflectedPiece, reflectedAxisStart.id);

    // Set reflected handles and remove knot types.
    p.knots[axisStartIndex].rH = reflectedAxisStart.rH;
    p.knots[axisStartIndex].rHI = reflectedAxisStart.rHI;
    p.knots[axisStartIndex].type = null;
    p.knots[axisEndIndex].lH = reflectedAxisEnd.lH;
    p.knots[axisEndIndex].lHI = reflectedAxisEnd.lHI;
    p.knots[axisEndIndex].type = null;

    // Add knots (add knots whose index is bigger than axisStartIndex first).
    const lastKnotsToAdd = [];
    let counter = 0;

    reflectedPiece.knots.forEach((knot, idx) => {
      // Ignore axisEnd
      if (idx === reflectedAxisEndIndex) return;

      if (idx < reflectedAxisEndIndex && idx !== reflectedAxisStartIndex) {
        lastKnotsToAdd.push(knot);
        return;
      }

      const seam = reflectedPiece.seam.find((s) => s.knotId === knot.id);
      const notches = reflectedPiece.notches.filter((notch) => notch.knotId === knot.id);

      // Ignore axisStart's knot but not its seam, notches, etc.
      if (idx !== reflectedAxisStartIndex) {
        p = this.addKnot(p, knot, axisStartIndex + counter);
        p = { ...p, notches: [...p.notches, ...notches] };

        if (seam) {
          p = { ...p, seam: [...p.seam, seam] };
        }
      } else {
        if (seam) {
          p = { ...p, seam: [...p.seam, { ...seam, knotId: mirror }] };
        }

        p = { ...p, notches: [...p.notches, ...notches.map((notch) => ({ ...notch, knotId: mirror }))] };
      }

      counter += 1;
    });

    lastKnotsToAdd.forEach((knot) => {
      const seam = reflectedPiece.seam.find((s) => s.knotId === knot.id);
      const notches = reflectedPiece.notches.filter((notch) => notch.knotId === knot.id);

      p = this.addKnot(p, knot, axisStartIndex + counter);

      if (seam) {
        p = { ...p, seam: [...p.seam, seam] };
      }

      p = { ...p, notches: [...p.notches, notches] };

      counter += 1;
    });

    if (reflectedPiece.drillHoles && Object.keys(reflectedPiece.drillHoles).length) {
      p = { ...p, drillHoles: { ...p.drillHoles, ...reflectedPiece.drillHoles } };
    }

    if (reflectedPiece.internalLines && Object.keys(reflectedPiece.internalLines).length) {
      p = {
        ...p,
        internalLines: {
          ...p.internalLines,
          ...Object.assign({}, ...Object.values(reflectedPiece.internalLines).map((internalLine) => {
            let { startWeld, endWeld } = internalLine;

            if (startWeld === reflectedAxisStart.id) startWeld = mirror;
            if (startWeld === reflectedAxisEnd.id) startWeld = axisEndId;
            if (endWeld === reflectedAxisStart.id) endWeld = mirror;
            if (endWeld === reflectedAxisEnd.id) endWeld = axisEndId;

            return { [internalLine.id]: { ...internalLine, startWeld, endWeld } };
          })),
        },
      };
    }

    // Remove mirror
    return { ...p, mirror: null };
  }

  static convertBezierJSCurvesToKnots(beziers, isClosedPath = false) {
    if (!beziers || !beziers.length) return null;

    let knotPosition = beziers[0].points[0];
    const knots = [createKnot(generate(), knotPosition.x, knotPosition.y)];

    for (let i = 0; i < beziers.length; i += 1) {
      knots[knots.length - 1] = SetRH(knots[knots.length - 1], beziers[i].points[1]);

      if (i === beziers.length - 1 && isClosedPath) {
        SetLH(knots[0], beziers[i].points[2]);
        break;
      }

      knotPosition = beziers[i].points[3]; // eslint-disable-line prefer-destructuring
      let nextKnot = createKnot(generate(), knotPosition.x, knotPosition.y);
      nextKnot = SetLH(nextKnot, beziers[i].points[2]);

      knots.push(nextKnot);
    }

    return knots;
  }

  static getOrderedKnotIdsWithIndex(knots, knotIds) {
    const knotIdsWithIndex = knotIds.map((knotId) => ({ id: knotId, index: knots.findIndex((knot) => knot.id === knotId) }))
      .sort((elementOne, elementTwo) => elementOne.index > elementTwo.index);

    const holes = [];

    knotIdsWithIndex.slice(1).forEach(({ index: knotIndex }, idx) => {
      // If indexes are not continous, add array position to holes.
      if (knotIndex - knotIdsWithIndex[idx].index > 1) holes.push(idx + 1);
    });

    const firstKnotsAreAfterLastKnots = holes.length === 1 && knotIdsWithIndex[0].index === 0 && knotIdsWithIndex[knotIdsWithIndex.length - 1].index === knots.length - 1;

    if (firstKnotsAreAfterLastKnots) {
      return {
        knotIdsWithIndex: [...knotIdsWithIndex.slice(holes[0]), ...knotIdsWithIndex.slice(0, holes[0])],
        knotsAreConsecutive: true,
      };
    }

    return {
      knotIdsWithIndex,
      knotsAreConsecutive: !holes.length,
    };
  }

  static getIntersectionsByLine(piece, line) {
    const segments = this.getSegments(piece);

    const beziers = [];
    for (let i = 0; i < segments.length; i += 1) {
      beziers.push(convertSegmentIntoBezierCurves(segments[i]));
    }

    const intersections = [];
    beziers.forEach((bezier, index) => {
      bezier.forEach((b, i) => {
        const obj = intersectCurveByLine(b, line);
        if (obj.flag === true) {
          intersections.push(
            {
              segment: segments[index],
              point: b.get(obj.t),
              t: obj.t,
              bezierIdx: i,
            }
          );
        }
      });
    });
    return intersections;
  }

  static getFirstIntersectionByLine(piece, line) {
    const intersections = this.getIntersectionsByLine(piece, line);
    let intersectionIndex = 0;
    let minDistance = 0;
    intersections.forEach((intersection, index) => {
      const distance = MathHelper.getDistance(line.p1, intersection.point);
      if (distance !== 0 && minDistance === 0) {
        minDistance = distance;
        intersectionIndex = index;
      } else if (distance && distance < minDistance) {
        intersectionIndex = index;
        minDistance = distance;
      }
    });

    return intersections[intersectionIndex];
  }

  static getLastIntersectionByLine(piece, line) {
    const intersections = this.getIntersectionsByLine(piece, line);

    let intersectionIndex = 0;
    let maxDistance = 0;
    intersections.forEach((intersection, index) => {
      const distance = MathHelper.getDistance(line.p1, intersection.point);
      if (index === 0) {
        maxDistance = distance;
      } else if (distance > maxDistance) {
        intersectionIndex = index;
        maxDistance = distance;
      }
    });

    return intersections[intersectionIndex];
  }

  static getSplitKnots(piece, knots, bezierIdx, t, prevIdIn = null, nextIdIn = null) {
    const prevId = !prevIdIn ? PieceHelper.getNextKnotId(piece, knots[0].id, bezierIdx) : prevIdIn;
    const nextId = !nextIdIn ? PieceHelper.getNextKnotId(piece, knots[0].id, bezierIdx + 1) : nextIdIn;

    const { left, right } = SegmentHelper.splitBezier(knots, bezierIdx, t);
    const prevRH = !knots[bezierIdx].rHI ? { ...left.points[1], id: prevId } : null;
    const nextLH = !knots[bezierIdx + 1].lHI ? { ...right.points[2], id: nextId } : null;

    const { x, y } = left.points[3];
    const lH = MathHelper.substractPoints(left.points[2], left.points[3]);
    const rH = MathHelper.substractPoints(right.points[1], left.points[3]);
    const newKnot = createKnot(generate(), x, y, false, lH, rH);
    const position = PieceHelper.getKnotIndex(piece, nextId);

    if (prevRH) prevRH.id = prevId;
    if (nextLH) nextLH.id = nextId;

    return {
      prevRH, newKnot, nextLH, position,
    };
  }


  // Implementation of Graham Scan algorithm
  static getConvexHull(knots) {
    const n = knots.length;
    let points = Object.assign([], knots);
    let ymin = points[0].y;
    let min = 0;
    for (let i = 1; i < n; i += 1) {
      const { y } = points[i];
      // Pick the bottom-most or chose the left
      // most point in case of tie
      if ((y < ymin) || (ymin === y && points[i].x < points[min].x)) {
        ymin = points[i].y;
        min = i;
      }
    }

    // Place the bottom-most point at first position
    this.swap(points, 0, min);

    // Sort n-1 points with respect to the first point.
    // A point p1 comes before p2 in sorted ouput if p2
    // has larger polar angle (in counterclockwise
    // direction) than p1
    const p0 = points[0];
    const pointsAux = points.slice(1);
    this.quickSort(pointsAux, 0, n - 2, p0);
    points = [p0, ...pointsAux];
    // If two or more points make same angle with p0,
    // Remove all but the one that is farthest from p0
    // Remember that, in above sorting, our criteria was
    // to keep the farthest point at the end when more than
    // one points have same angle.
    let m = 1; // Initialize size of modified array
    for (let i = 1; i < n; i += 1) {
      // Keep removing i while angle of i and i+1 is same
      // with respect to p0
      while (i < n - 1 && this.orientation(p0, points[i], points[i + 1]) === 0) {
        i += 1;
      }
      points[m] = points[i];
      m += 1; // Update size of modified array
    }
    // If modified array of points has less than 3 points,
    // convex hull is not possible
    if (m < 3) return;

    // Create an empty stack and push first three points
    // to it.
    const stackPoints = [];
    stackPoints.push(points[0]);
    stackPoints.push(points[1]);
    stackPoints.push(points[2]);
    // Process remaining n-3 points
    for (let i = 3; i < m; i += 1) {
      // Keep removing top while the angle formed by
      // points next-to-top, top, and points[i] makes
      // a non-left turn
      while (this.orientation(this.nextToTop(stackPoints), stackPoints[stackPoints.length - 1], points[i]) !== 1) {
        stackPoints.pop();
      }
      stackPoints.push(points[i]);
    }
    // eslint-disable-next-line consistent-return
    return stackPoints;
  }

  // A utility function to find next to top in a stack
  static nextToTop(stackPoints) {
    const res = stackPoints[stackPoints.length - 2];
    return res;
  }

  // A utility function to swap two points
  static swap(stackPoints, i, j) {
    // eslint-disable-next-line no-param-reassign
    [stackPoints[i], stackPoints[j]] = [stackPoints[j], stackPoints[i]];
  }

  // A utility function to return square of distance
  // between p1 and p2
  static distSq(p1, p2) {
    return ((p1.x - p2.x) ** 2) + ((p1.y - p2.y) ** 2);
  }

  // To find orientation of ordered triplet (p, q, r).
  // The function returns following values
  // 0 --> p, q and r are colinear
  // 1 --> Counterclockwise
  // 2 --> Clockwise
  static orientation(p, q, r) {
    const val = ((q.y - p.y) * (r.x - q.x)) - ((q.x - p.x) * (r.y - q.y));
    if (val === 0) return 0; // colinear
    return (val > 0) ? 1 : 2; // clock or counterclock wise
  }

  // A function used by library function qsort() c++ to sort an array of
  // points with respect to the first point
  static compare(p0, p1, p2) {
    const o = this.orientation(p0, p1, p2);
    if (o === 0) return (this.distSq(p0, p2) >= this.distSq(p0, p1)) ? -1 : 1;
    return (o === 2) ? -1 : 1;
  }

  static partition(arr, low, high, p0) {
    const pivot = arr[high];
    let i = low - 1;

    for (let j = low; j <= high - 1; j += 1) {
      // If current element is smaller than or
      // equal to pivot
      if (this.compare(p0, arr[j], pivot) === 1) {
        i += 1; // increment index of smaller element
        this.swap(arr, i, j);
      }
    }
    this.swap(arr, i + 1, high);
    return i + 1;
  }

  // The main function that implements QuickSort
  //  arr[] --> Array to be sorted,
  //   low  --> Starting index,
  //   high  --> Ending index
  static quickSort(arr, low, high, p0) {
    if (low < high) {
      /* pi is partitioning index, arr[p] is now
      at right place */
      const pi = this.partition(arr, low, high, p0);

      // Separately sort elements before
      // partition and after partition
      this.quickSort(arr, low, pi - 1, p0);
      this.quickSort(arr, pi + 1, high, p0);
    }
  }

  static isKnotInsidePiece(point, bezierSegments) {
    const extreme = { x: 1000000, y: point.y };
    let count = 0;
    const line = { p1: point, p2: extreme };
    bezierSegments.forEach((beziers) => {
      beziers.forEach((bezier) => {
        const intersected = bezier.intersects(line);
        if (intersected.length > 0) count += intersected.length;
      });
    });
    return (count % 2 === 1);
  }

  static arePiecesClosed(pieces) {
    let areClosed = true;
    pieces.forEach((piece) => {
      const { isClosed } = piece;
      if (!isClosed) areClosed = false;
    });
    return areClosed;
  }

  static areLinesCorrect(lines) {
    let areCorrect = true;
    lines.forEach((line) => {
      const nKnots = line.knots.length;
      if (nKnots < 2) areCorrect = false;
    });
    return areCorrect;
  }

  /**
   * I used this code to implement notches but we need to change to getGradedKnots
   * to handle a better format in all the app
   */
  static getGradeBeziersForKnots({ centroid, knots, index, offset, color }) {
    const beziers = [];
    const last = knots.length - 1;
    for (let i = 0; i <= last; i++) {
      const knot = knots[i];
      const next = i == last ? knots[0] : knots[i + 1];
      let distance = MathHelper.getDistance(knot, next);
      const rH = GetRelRH(knot);
      const lH = GetRelLH(next);
      // Initializse the arrays on these
      if (typeof knot.grades === 'undefined') {
        knot.grades = [];
      }
      if (typeof next.grades === 'undefined') {
        next.grades = [];
      }

      // If this knot did not come with grades, we interpolate
      if (!knot.grades[index]) {
        knot.grades[index] = PieceHelper.getKnotOffsetAsDelta(centroid, knot, offset);
      }

      // If the next knot did not come with grades, we interpolate
      if (!next.grades[index]) {
        next.grades[index] = PieceHelper.getKnotOffsetAsDelta(centroid, next, offset);
      }

      // Add the offset to the knot to get the absolute positions
      const knotG = MathHelper.addPoints(knot, knot.grades[index]);
      const nextG = MathHelper.addPoints(next, next.grades[index]);

      // Get the distance betweeen the graded knot absolut positions
      let distanceG = MathHelper.getDistance(knotG, nextG);

      // if we make a division between 0 and 0 the result is NaN
      if (distanceG === 0 && distance === 0) {
        distanceG = 1;
        distance = 1;
      }

      const ratio = distanceG / distance;


      const knotGRightHandle = MathHelper.addPoints(knotG, MathHelper.scalePoint(rH, ratio));
      const nextGLeftHandle = MathHelper.addPoints(nextG, MathHelper.scalePoint(lH, ratio));

      // add the graded knot to the grade lines array
      beziers.push({
        points: [knotG, knotGRightHandle, nextGLeftHandle, nextG],
        color,
      });
    }

    return beziers;
  }

  static getArrowhead(piece, scale, printScale = 1) {
    const { knots, arrowHead, arrowTail } = piece;
    const arrowScale = scale > 3 ? 2.9 : Math.max(scale, 0.33);
    const angle = Math.PI / 350 * (10 ** (Math.round(Math.log10(arrowScale)))) * printScale;
    const distance = 30 * (10 ** (Math.round(Math.log10(arrowScale)))) * printScale;
    let pathArrowHead;
    let pathArrowTail;
    if (arrowHead) {
      const startKnot = knots[0];
      const nextCorner = knots[1];
      const bezier = convertSegmentIntoBezierCurves([startKnot, nextCorner]);
      const secondKnot = bezier[0].get(0.01);
      const foco = MathHelper.getNormalizedVector(MathHelper.substractPoints(secondKnot, startKnot));
      const projectedFocoToStart = MathHelper.addPoints(MathHelper.scalarMultiplication(foco, distance), startKnot);
      const projectedFoco = MathHelper.scalarMultiplication(foco, distance + 1);
      const normalProjectedFoco = { x: -projectedFoco.y, y: projectedFoco.x };
      const r = distance / Math.cos(angle);
      const high = r * Math.sin(angle);
      const arrowCorner1 = MathHelper.addPoints(MathHelper.scalarMultiplication(normalProjectedFoco, high), projectedFocoToStart);
      const arrowCorner2 = MathHelper.addPoints(MathHelper.scalarMultiplication(normalProjectedFoco, -high), projectedFocoToStart);
      pathArrowHead = `M${arrowCorner1.x * scale} ${arrowCorner1.y * scale} L${startKnot.x * scale} ${startKnot.y * scale} L${arrowCorner2.x * scale} ${arrowCorner2.y * scale} Z`;
    }
    if (arrowTail) {
      const endKnot = knots[knots.length - 1];
      const prevCorner = knots[knots.length - 2];
      const bezier = convertSegmentIntoBezierCurves([prevCorner, endKnot]);
      const secondKnot = bezier[0].get(0.99);
      const foco = MathHelper.getNormalizedVector(MathHelper.substractPoints(secondKnot, endKnot));
      const projectedFocoToEnd = MathHelper.addPoints(MathHelper.scalarMultiplication(foco, distance), endKnot);
      const projectedFoco = MathHelper.scalarMultiplication(foco, distance + 1);
      const normalProjectedFoco = { x: -projectedFoco.y, y: projectedFoco.x };
      const r = distance / Math.cos(angle);
      const high = r * Math.sin(angle);
      const arrowCorner1 = MathHelper.addPoints(MathHelper.scalarMultiplication(normalProjectedFoco, high), projectedFocoToEnd);
      const arrowCorner2 = MathHelper.addPoints(MathHelper.scalarMultiplication(normalProjectedFoco, -high), projectedFocoToEnd);
      pathArrowTail = `M${arrowCorner1.x * scale} ${arrowCorner1.y * scale} L${endKnot.x * scale} ${endKnot.y * scale} L${arrowCorner2.x * scale} ${arrowCorner2.y * scale} Z`;
    }
    return [pathArrowHead, pathArrowTail];
  }

  static piecesInsidePieces(piecesJS, piecesToMove = []) {
    const pieces = this.clearPiecesInside(Object.values(piecesJS));
    const piecesInsideIds = new Set();
    const piecesTM = new Set(piecesToMove);
    const piecesInsideToMove = new Set();
    for (let i = 0; i < pieces.length - 1; i += 1) {
      const boundsA = this.getBounds(pieces[i]);
      for (let j = i + 1; j < pieces.length; j += 1) {
        const boundsB = this.getBounds(pieces[j]);
        if (boundsA.x < boundsB.x && (boundsB.x + boundsB.w) < (boundsA.x + boundsA.w)
        && boundsA.y < boundsB.y && (boundsB.y + boundsB.h) < (boundsA.y + boundsA.h)) {
          piecesInsideIds.add(pieces[j].id);
          pieces[i].piecesInside.add(pieces[j].id);
          if (piecesTM.has(pieces[i].id)) piecesInsideToMove.add(pieces[j].id);
        } else if (boundsA.x > boundsB.x && (boundsB.x + boundsB.w) > (boundsA.x + boundsA.w)
        && boundsA.y > boundsB.y && (boundsB.y + boundsB.h) > (boundsA.y + boundsA.h)) {
          piecesInsideIds.add(pieces[i].id);
          pieces[j].piecesInside.add(pieces[i].id);
          if (piecesTM.has(pieces[j].id)) piecesInsideToMove.add(pieces[i].id);
        }
      }
    }
    return { piecesInsideIds, piecesInsideToMove, piecesUpdated: pieces };
  }

  static clearPiecesInside(pieces) {
    for (let i = 0; i < pieces.length; i += 1) {
      // eslint-disable-next-line no-param-reassign
      pieces[i].piecesInside = new Set();
    }
    return pieces;
  }

  static pieceInsidePiece(pieceA, pieceB) {
    const boundsA = this.getBounds(pieceA);
    const boundsB = this.getBounds(pieceB);
    const inside = (boundsA.x < boundsB.x && (boundsB.x + boundsB.w) < (boundsA.x + boundsA.w)
      && boundsA.y < boundsB.y && (boundsB.y + boundsB.h) < (boundsA.y + boundsA.h));
    return inside;
  }

  static haveMirrorTheIntersectedLine(segmentIntersect) {
    let isMirrorStart = false;
    let isMirrorEnd = false;
    segmentIntersect.forEach((knot) => {
      if (knot.type === 'MIRROR_START') {
        isMirrorStart = true;
      }

      if (knot.type === 'MIRROR_END') {
        isMirrorEnd = true;
      }
    });

    if (isMirrorStart && isMirrorEnd) {
      return true;
    }

    return false;
  }

  static getKnotOffsetAsDelta(centroid, knot, offset) {
    const d = MathHelper.getDistance(knot, centroid);
    const f = offset / d;
    const g = MathHelper.interpolatePointsCorrect(knot, centroid, -f);
    return MathHelper.substractPoints(g, knot);
  }

  /**
   * Get a new set of knots at the offset from the centroid.
   */
  static getGradedKnots({ centroid, knots, index, offset, isClosed }) {
    const gradeKnots = [];

    // Frist we clone the knots
    // If no grade is found, we will return the base knots
    for (let i = 0; i < knots.length; i++) {
      gradeKnots[i] = { ...knots[i] };
    }

    const last = isClosed ? knots.length : knots.length - 1;
    for (let i = 0; i < last; i++) {
      const ni = (isClosed && i == knots.length - 1) ? 0 : i + 1;
      const knot = knots[i];
      const next = knots[ni];
      const distance = MathHelper.getDistance(knot, next);

      // Initializse the arrays on these
      if (typeof knot.grades === 'undefined') {
        knot.grades = [];
      }
      if (typeof next.grades === 'undefined') {
        next.grades = [];
      }

      // If this knot did not come with grades, we interpolate
      let knotGrade = knot.grades[index];
      if (!knotGrade) {
        knotGrade = this.getKnotOffsetAsDelta(centroid, knot, offset);
      }

      // If the next knot did not come with grades, we interpolate
      let nextGrade = next.grades[index];
      if (!nextGrade) {
        nextGrade = this.getKnotOffsetAsDelta(centroid, next, offset);
      }

      // Add the offset to the knot to get the absolute positions
      const knotG = MathHelper.addPoints(knot, knotGrade);
      const nextG = MathHelper.addPoints(next, nextGrade);

      // Get the distance betweeen the graded knot absolut positions
      const distanceG = MathHelper.getDistance(knotG, nextG);


      let ratio = distanceG / distance;
      // eslint-disable-next-line no-restricted-globals
      // This sometimes happens when we are using a mirror.
      if (ratio === Infinity || isNaN(ratio)) {
        ratio = 1;
      }

      // add the graded knot to the grade lines array
      if (knotG && knotG.x && knotG.y) {
        gradeKnots[i].x = knotG.x;
        gradeKnots[i].y = knotG.y;
        gradeKnots[i].rH = knot.rH ? MathHelper.scalePoint(knot.rH, ratio) : null;
      }

      if (nextG && nextG.x && nextG.y) {
        gradeKnots[ni].x = nextG.x;
        gradeKnots[ni].y = nextG.y;
        gradeKnots[ni].lH = next.lH ? MathHelper.scalePoint(next.lH, ratio) : null;
      }

    }
    return gradeKnots;
  }

  static getPieceKnotsAtGrade(piece, gradeIndex) {
    const centroid = this.getCentroid(piece);
    const grade = piece.get('grades').get(gradeIndex);
    const knots = piece.get('knots');

    // We add the first knot to the end because the nature of 'getGradedKnots'
    knots.push(knots.get(0));

    return this.getGradedKnots(centroid, knots, gradeIndex, grade.get('offset'), grade.get('color'));
  }


  static clonePieceAtGrade(piece, gradeIndex) {
    piece = changeIds(piece);
    return { ...piece,
      knots: this.getPieceKnotsAtGrade(piece, gradeIndex),
    };
  }
}
